import { BookOpen, Workflow, ArrowLeftRight, Layers, ArrowRight, Bot, Brain, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

const features = [
  {
    icon: BookOpen,
    color: "mint" as const,
    title: "KI-Wissensdatenbank",
    description: "Antworten aus der zentralen Wissensbasis",
  },
  {
    icon: Workflow,
    color: "orange" as const,
    title: "AI Workflows",
    description: "Prozesse direkt aus der Konversation auslösen",
  },
  {
    icon: ArrowLeftRight,
    color: "mint" as const,
    title: "Nahtlose Übergabe",
    description: "Zwischen KI und Mensch wechseln",
  },
  {
    icon: Layers,
    color: "orange" as const,
    title: "Strukturierte Verarbeitung",
    description: "Daten kontextbezogen weiterverarbeiten",
  },
];

const crossLinks = [
  {
    icon: Bot,
    title: "AI Agent & KI Assistent",
    to: "/produkte/ai-agent",
  },
  {
    icon: Brain,
    title: "KI-Wissensdatenbank",
    to: "/produkte/ai-knowledge-base",
  },
  {
    icon: Zap,
    title: "AI Workflows & Automation",
    to: "/produkte/ai-automation-workflows",
  },
];

const colorMap = {
  mint: "bg-chathero-mint/15 text-chathero-mint",
  orange: "bg-chathero-orange/15 text-chathero-orange",
};

const EineInboxSection = () => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="text-center mb-8">
                <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
                  Plattform-Vernetzung
                </Badge>

                <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                  <span className="text-foreground">Eine Inbox – verbunden mit der gesamten Plattform</span>
                </h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                Die Unified Inbox ist kein isoliertes Tool, sondern Teil eines integrierten Systems. Jede Konversation kann:
              </p>

              {/* 2x2 Feature Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                      className={cn(
                        "bg-background rounded-2xl p-5 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] transition-all",
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                      )}
                      style={{
                        transitionDelay: isVisible ? `${200 + index * 100}ms` : "0ms",
                        transitionDuration: "600ms",
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
                            colorMap[feature.color]
                          )}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground mb-1">{feature.title}</h3>
                          <p className="text-muted-foreground text-xs leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mint highlight box */}
              <div
                className={cn(
                  "bg-chathero-mint/10 border border-chathero-mint/20 rounded-2xl p-5 transition-all duration-500",
                  isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
                )}
                style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
              >
                <p className="text-sm text-primary font-medium">
                  Chathero verbindet Ihre Inbox mit KI, Wissen und Prozessen – in einer Plattform.
                </p>
              </div>
            </div>

            {/* Right - Cross-Link Cards */}
            <div className="flex flex-col gap-4">
              {crossLinks.map((link, index) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={index}
                    to={link.to}
                    className={cn(
                      "group bg-background rounded-2xl p-5 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] hover:border-chathero-mint/40 hover:shadow-[0_4px_24px_rgba(0,196,180,0.15)] transition-all duration-300",
                      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                    )}
                    style={{
                      transitionDelay: isVisible ? `${300 + index * 150}ms` : "0ms",
                      transitionDuration: "600ms",
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-chathero-mint/15 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-chathero-mint" />
                      </div>
                      <span className="text-sm font-semibold text-foreground flex-1">{link.title}</span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-chathero-mint group-hover:translate-x-1 transition-all" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EineInboxSection;
